import React from 'react'
import { checkDuplicateEmail } from '../../Registration/CreateAccount/service'
import * as Yup from 'yup'
import { Modal, ModalVariant, Button, Input } from '@paro.io/base-ui'

interface Client {
    firstName: string
    lastName: string
    email: string
    company: string
}

const validationSchema = Yup.object({
    firstName: Yup.string().max(20, 'First name must be at most 20 characters').required('Required'),
    lastName: Yup.string().max(20, 'Last name must be at most 20 characters').required('Required'),
    company: Yup.string().max(100, 'Business name must be at most 100 characters').required('Required'),
    email: Yup.string()
        .email('Please enter a valid email.')
        .min(6, 'Please enter a valid email.')
        .required('Required'),
});

interface IAddNewClientModalProps {
    open: boolean
    onClose: () => void
    onClickAddClient: (client: Client) => void
}

const AddNewClientModal = ({ open, onClose, onClickAddClient }: IAddNewClientModalProps) => {
    const defaultFormValues = {
        firstName: '',
        lastName: '',
        email: '',
        company: '',
    }

    const [formValues, setFormValues] = React.useState(defaultFormValues)
    const [emailError, setEmailError] = React.useState<string | null>(null)

    const [fieldErrors, setFieldErrors] = React.useState<{ [key: string]: string }>({})

    const isDisabled =
        !!emailError ||
        Object.values(fieldErrors).some(Boolean)

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (emailError || Object.values(fieldErrors).some(Boolean)) return;
        onClickAddClient(formValues);
        setFormValues(defaultFormValues);
        setEmailError(null);
        setFieldErrors({});
    }

    const handleEmailBlur = async () => {
        // Validate email with Yup first
        try {
            await validationSchema.validateAt('email', formValues);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setEmailError(msg);
            return;
        }
        // Only check duplicate if email is valid
        const { valid, message } = await checkDuplicateEmail(formValues.email);
        if (!valid) {
            setEmailError(message || 'User with this email already exists.');
        } else {
            setEmailError(null);
        }
    }

    const handleFieldBlur = async (field: string) => {
        try {
            await validationSchema.validateAt(field, formValues);
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setFieldErrors(prev => ({ ...prev, [field]: msg }));
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                onClose();
                setFormValues(defaultFormValues);
            }}
            label="Add New Client"
            size="sm"
            variant={ModalVariant.passive}
        >
            <form className="px-4 py-3" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 mb-3">
                    <Input
                        name="firstName"
                        label="First Name *"
                        placeholder="First Name"
                        value={formValues.firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFormValues({ ...formValues, firstName: e.target.value });
                            setFieldErrors(prev => ({ ...prev, firstName: '' }));
                        }}
                        onBlur={() => handleFieldBlur('firstName')}
                        isInvalid={!!fieldErrors.firstName}
                        isInvalidText={fieldErrors.firstName || undefined}
                    />
                    <Input
                        name="lastName"
                        label="Last Name *"
                        placeholder="Last Name"
                        value={formValues.lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFormValues({ ...formValues, lastName: e.target.value });
                            setFieldErrors(prev => ({ ...prev, lastName: '' }));
                        }}
                        onBlur={() => handleFieldBlur('lastName')}
                        isInvalid={!!fieldErrors.lastName}
                        isInvalidText={fieldErrors.lastName || undefined}
                    />
                </div>
                <div className="mb-3">
                    <Input
                        name="email"
                        label="Contact Email *"
                        placeholder="Contact Email"
                        type="email"
                        value={formValues.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFormValues({ ...formValues, email: e.target.value });
                            setEmailError(null);
                        }}
                        onBlur={handleEmailBlur}
                        isInvalid={!!emailError}
                        isInvalidText={emailError ? emailError : undefined}
                    />
                </div>
                <div className="mb-6">
                    <Input
                        name="company"
                        label="Company Name *"
                        placeholder="Company Name"
                        value={formValues.company}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFormValues({ ...formValues, company: e.target.value });
                            setFieldErrors(prev => ({ ...prev, company: '' }));
                        }}
                        onBlur={() => handleFieldBlur('company')}
                        isInvalid={!!fieldErrors.company}
                        isInvalidText={fieldErrors.company || undefined}
                    />
                </div>
                <div className="flex justify-between gap-3 pt-4 border-t-2">
                    <Button
                        label="Cancel"
                        color="default"
                        onClick={() => {
                            onClose();
                            setFormValues(defaultFormValues);
                        }}
                    />
                    <Button label="Add Client" color="primary" type="submit" disabled={isDisabled} />
                </div>
            </form>
        </Modal>
    )
}

export default AddNewClientModal